import { Injectable, NgZone, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { environment } from '../../environments/environment';
import { PublicPaymentService } from '../../api/gen';
import { fetchStationHubToken, readStationProxy } from '../station-proxy';
import {
  CoinEndReason,
  CoinPaymentStarted,
  CoinProgressMessage,
  FiscalReceiptReadyMessage,
  NearMatchDetection,
  PaymentDoneMessage,
  StationConfig,
  TerminalOpenFee,
  TerminalPaymentStarted,
  VehicleDetection,
  WalletRedemption,
} from '../models/terminal-payment.models';

/**
 * Talks to the backend for this station.
 *
 * REST goes through the generated OpenAPI client (`PublicPaymentService`); this service maps its
 * all-optional DTOs to the app's clean types so the UI never juggles `undefined`. The SignalR side stays
 * hand-rolled — outcomes are pushed, not requested.
 *
 * Two channels, on purpose:
 *  - REST to quote + arm a charge (`terminal-*`). A failed call means nothing was charged; the parker retries.
 *  - the PaymentTerminalHub for results (`PaymentDone`, `FiscalReceiptReady`). The app is a *listener* here.
 *
 * This app never learns an outcome by guessing. If the hub drops mid-flow it re-derives the truth from
 * `terminal-status/{id}` rather than assume the attempt it was watching is still running.
 */
@Injectable({ providedIn: 'root' })
export class PaymentTerminalService {
  private readonly api = inject(PublicPaymentService);
  private readonly zone = inject(NgZone);

  /**
   * Resolved terminal id.
   *
   * Production (Azure SWA): provisioned once via QR-code URL
   *   → `?terminalId=xxx` captured by main.ts → localStorage.
   *
   * Local dev: falls back to `environment.terminalId` (placeholder).
   */
  readonly terminalId: string =
    localStorage.getItem('jjames-terminalId') ?? environment.terminalId;

  /** Latest hub pushes, exposed as signals so the component reacts without manual subscriptions. */
  readonly paymentDone = signal<PaymentDoneMessage | null>(null);
  readonly fiscalReceipt = signal<FiscalReceiptReadyMessage | null>(null);
  readonly coinProgress = signal<CoinProgressMessage | null>(null);
  readonly hubConnected = signal(false);

  private hub?: HubConnection;

  // ------------------------------------------------------------------ hub

  /** Connect + join this terminal's group. Idempotent — safe to call on app start. */
  async connect(): Promise<void> {
    if (this.hub && this.hub.state !== HubConnectionState.Disconnected) {
      return;
    }

    // On a kiosk with a signing proxy, the hub connection is signed in as this terminal: the proxy hands out a
    // fresh token per connect, and SignalR sends it on negotiate. Without one the connection is anonymous,
    // which the backend allows only until this terminal is enforced.
    const proxy = readStationProxy();
    this.hub = new HubConnectionBuilder()
      .withUrl(environment.paymentHubUrl, proxy ? { accessTokenFactory: () => fetchStationHubToken(proxy) } : {})
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    // SignalR callbacks fire outside Angular's zone; hop back in so signals update the view.
    this.hub.on('PaymentDone', (msg: PaymentDoneMessage) =>
      this.zone.run(() => this.paymentDone.set(msg)),
    );
    this.hub.on('FiscalReceiptReady', (msg: FiscalReceiptReadyMessage) =>
      this.zone.run(() => this.fiscalReceipt.set(msg)),
    );
    this.hub.on('CoinProgress', (msg: CoinProgressMessage) =>
      this.zone.run(() => this.coinProgress.set(msg)),
    );

    // The Pi and this app join the SAME terminal group, so the backend's commands to the Pi are
    // delivered here too. We are not the reader and must never act on them — but without a
    // registered handler @microsoft/signalr logs "No client method with the name
    // 'startterminalpayment' found" for every single payment, which buries the warnings that do
    // matter in a kiosk console nobody can scroll. Register them as explicit no-ops, mirroring what
    // the Pi does for PaymentDone / FiscalReceiptReady, which are ours and not its.
    this.hub.on('StartTerminalPayment', () => { /* for the Pi */ });
    this.hub.on('AbortTerminalPayment', () => { /* for the Pi */ });
    this.hub.on('ReverseTerminalPayment', () => { /* for the Pi */ });
    this.hub.on('StartCoinAcceptance', () => { /* for the Pi */ });
    this.hub.on('StopCoinAcceptance', () => { /* for the Pi */ });

    this.hub.onreconnected(() => this.zone.run(() => void this.joinTerminal()));
    this.hub.onclose(() => this.zone.run(() => this.hubConnected.set(false)));

    await this.hub.start();
    await this.joinTerminal();
    this.hubConnected.set(true);
  }

  private async joinTerminal(): Promise<void> {
    if (this.hub?.state === HubConnectionState.Connected) {
      await this.hub.invoke('JoinTerminal', this.terminalId);
    }
  }

  // ------------------------------------------------------------------ REST

  /** Station branding (site name, hotline, languages, plate prefix). Loaded once on idle. */
  async stationConfig(): Promise<StationConfig> {
    const d = await firstValueFrom(
      this.api.publicPaymentTerminalStationConfigTerminalIdGet(this.terminalId),
    );
    return {
      spaceName: d.spaceName ?? '',
      hotline: d.hotline ?? null,
      countryPrefix: d.countryPrefix ?? 'AT',
      defaultLanguage: d.defaultLanguage ?? 'de',
      languages: d.languages ?? ['de', 'en'],
      logoUrl: d.logoUrl ?? null,
      coinAcceptor: d.coinAcceptor
        ? {
            enabled: d.coinAcceptor.enabled ?? false,
            online: d.coinAcceptor.online ?? false,
            acceptedCoinValuesInCents: d.coinAcceptor.acceptedCoinValuesInCents ?? [],
          }
        : null,
    };
  }

  /**
   * Plates with an active parking process in this station's space — the entry screen's type-ahead source.
   * Loaded once when the parker starts typing; the component filters it locally as they type. `search` is an
   * optional server-side narrowing hint. Scoped to this terminal's space, so it never lists other sites.
   */
  async activeParkedPlates(search?: string): Promise<string[]> {
    const d = await firstValueFrom(
      this.api.publicPaymentTerminalActivePlatesTerminalIdGet(this.terminalId, search || undefined),
    );
    return d.plates ?? [];
  }

  /**
   * Plate-not-found recovery: vehicles parked in this space whose plate is closest to what was typed. Maps
   * the backend's raw score (0..1) + stay-start instant into display strings for the recovery cards.
   */
  async nearMatches(plate: string): Promise<NearMatchDetection[]> {
    const d = await firstValueFrom(
      this.api.publicPaymentTerminalNearMatchesTerminalIdPlateGet(this.terminalId, plate),
    );
    return (d.matches ?? []).map((m) => ({
      plate: m.plate ?? '',
      matchScore: `${Math.round((m.score ?? 0) * 1000) / 10}%`,
      time: this.formatTime(m.startedAt),
      parkingProcessId: (m as any).parkingProcessId ?? '',
      vehicleImageUrl: (m as any).vehicleImageUrl ?? null,
      plateImageUrl: (m as any).plateImageUrl ?? null,
    }));
  }

  /** ISO instant → `HH:mm` (de-AT). Empty for null/unparseable input. */
  private formatTime(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
  }

  /** Latest ANPR detection (photo + detected plate) so the parker can confirm / correct their plate. */
  async vehicleDetection(plate: string): Promise<VehicleDetection> {
    const d = await firstValueFrom(
      this.api.publicPaymentTerminalVehicleImageTerminalIdLicencePlateGet(this.terminalId, plate),
    );
    return {
      hasImage: d.hasImage ?? false,
      plateNumber: d.plateNumber ?? plate,
      vehicleImageUrl: d.vehicleImageUrl ?? null,
      plateImageUrl: d.plateImageUrl ?? null,
      detectedAt: d.detectedAt ?? null,
    };
  }

  /** Quote the short-term open fee for a plate. Side-effect-free; the fee is derived server-side. */
  async quote(plate: string): Promise<TerminalOpenFee> {
    const d = await firstValueFrom(
      this.api.publicPaymentTerminalOpenFeeTerminalIdLicencePlateGet(this.terminalId, plate),
    );
    return {
      hasOpenFee: d.hasOpenFee ?? false,
      alreadyPaid: d.alreadyPaid ?? false,
      amountInCents: d.amountInCents ?? null,
      currency: d.currency ?? null,
      reason: d.reason ?? null,
      pricedAt: d.pricedAt ?? null,
      isCovered: d.isCovered ?? false,
      stay: d.startedAt
        ? {
            startedAt: d.startedAt,
            endedAt: d.endedAt ?? null,
            parkingSpaceName: d.parkingSpaceName ?? null,
            parkingZoneName: d.parkingZoneName ?? null,
          }
        : null,
      redeemableCreditInCents: d.redeemableCreditInCents ?? 0,
    };
  }

  /**
   * Arm the reader. The charge happens after this returns; results arrive over the hub.
   *
   * `pricedAt` is echoed from the quote so the backend charges for the duration at quote time — a slow
   * confirm/tap can't push a 59-minute stay across an hour boundary.
   */
  async start(plate: string, amountInCents?: number, pricedAt?: string | null): Promise<TerminalPaymentStarted> {
    const d = await firstValueFrom(
      this.api.publicPaymentTerminalStartPost({
        terminalId: this.terminalId,
        licensePlate: plate,
        amountInCents: amountInCents ?? null,
        pricedAt: pricedAt ?? null,
      }),
    );
    return {
      paymentRequestId: d.paymentRequestId ?? '',
      amountInCents: d.amountInCents ?? 0,
      currency: d.currency ?? 'EUR',
    };
  }

  /**
   * Open the station's coin slot for what the stay owes. Priced server-side like a card arm; the
   * coins arrive as `CoinProgress` pushes and the result as a `PaymentDone` for this request id.
   */
  async startCoins(plate: string, amountInCents?: number, pricedAt?: string | null): Promise<CoinPaymentStarted> {
    const d = await firstValueFrom(
      this.api.publicPaymentCoinStartPost({
        terminalId: this.terminalId,
        licensePlate: plate,
        amountInCents: amountInCents ?? null,
        pricedAt: pricedAt ?? null,
      }),
    );
    return {
      paymentRequestId: d.paymentRequestId ?? '',
      amountInCents: d.amountInCents ?? 0,
      currency: d.currency ?? 'EUR',
      acceptedCoinValuesInCents: d.acceptedCoinValuesInCents ?? [],
    };
  }

  /** Close the coin slot: `SwitchToCard` ("Rest mit Karte") or `Cancel`. The coins are booked from the Pi's report. */
  async stopCoins(paymentRequestId: string, reason: Extract<CoinEndReason, 'SwitchToCard' | 'Cancel'>): Promise<void> {
    await firstValueFrom(
      this.api.publicPaymentCoinStopPost({ terminalId: this.terminalId, paymentRequestId, reason }),
    );
  }

  /**
   * Spend credit on the stay — only ever on the parker's tap. `redemptionId` is per tap, so a retried
   * request spends nothing twice; never more than `amountInCents` (the figure shown) is spent.
   */
  async redeemCredit(
    redemptionId: string,
    plate: string,
    amountInCents: number,
    pricedAt?: string | null,
  ): Promise<WalletRedemption> {
    const d = await firstValueFrom(
      this.api.publicPaymentWalletRedeemPost({
        terminalId: this.terminalId,
        redemptionId,
        licensePlate: plate,
        amountInCents,
        pricedAt: pricedAt ?? null,
      }),
    );
    return {
      redeemedInCents: d.redeemedInCents ?? 0,
      remainingDueInCents: d.remainingDueInCents ?? 0,
      covered: d.covered ?? false,
    };
  }

  /** Reconnect fallback: re-derive an attempt's state instead of trusting stale UI. */
  status(paymentRequestId: string) {
    return firstValueFrom(this.api.publicPaymentTerminalStatusPaymentRequestIdGet(paymentRequestId));
  }

  /**
   * Cancel an armed authorisation — the parker pressed cancel while the reader was waiting for the card.
   * The backend relays the abort to the Pi (ZVT `06 1E`). Best-effort: a late `Paid` may still arrive over
   * the hub and is handled there, so the caller resets the UI regardless of whether this resolves.
   */
  async abort(paymentRequestId: string): Promise<void> {
    await firstValueFrom(this.api.publicPaymentTerminalAbortPost({ paymentRequestId }));
  }

  /**
   * Correct a misread license plate.  Called when the parker taps "that's my car" on a near-match card.
   * Reassigns the parking process to the correct plate (creating a guest parker if needed).
   */
  async correctPlate(parkingProcessId: string, correctedPlateNumber: string): Promise<void> {
    await firstValueFrom(this.api.publicPaymentTerminalCorrectPlatePost({
      parkingProcessId,
      correctedPlateNumber,
    } as any));
  }
}
